import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { ChevronDown, FileImage, Menu, X } from "lucide-react";
import { useState } from "react";
import Monogram from "./Monogram";
import { useUploadedFlyers } from "../lib/flyers";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuPortal,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from "./ui/dropdown-menu";

const FLYER_LINKS = [
  { to: "/flyers#circulate-the-dollar", label: "Circulate the Dollar", hint: "Print flyer · QR to membership pledge", id: "flyer-circulate" },
  { to: "/flyers#rally", label: "Rally of the Minds", hint: "Official rally flyer", id: "flyer-rally" },
  { to: "/flyers#social", label: "Social Media Flyers", hint: "Story · Square · Landscape", id: "flyer-social" },
  { to: "/flyers", label: "All Flyers", id: "flyers" },
];

const NAV = [
  { to: "/", label: "Mission", id: "mission" },
  { to: "/directory", label: "Directory", id: "directory" },
  {
    key: "fund", label: "Fund", heading: "Trillion Dollar Spending Power Fund",
    items: [
      { to: "/pledge", label: "Fund Pledge", hint: "Give to the $50M community fund", id: "fund-pledge" },
      { to: "/founders", label: "Founders", hint: "Black-owned ventures seeking backing", id: "founders" },
      { to: "/ambassadors", label: "Ambassadors", hint: "Refer friends & climb the tiers", id: "ambassadors" },
    ],
  },
  {
    key: "community", label: "Community",
    items: [
      { to: "/events", label: "Events", id: "events" },
      { to: "/rally", label: "Rally", id: "rally" },
      { to: "/spotlight", label: "Spotlight", id: "spotlight" },
      { to: "/news", label: "News", id: "news" },
      { to: "/shop", label: "Shop", id: "shop" },
    ],
  },
  {
    key: "credit-union", label: "Credit Union", heading: "Proposed Trillion Dollar Spending Power Federal Credit Union",
    items: [
      { to: "/credit-union", label: "Overview", id: "credit-union" },
      { to: "/credit-union#subscribe", label: "Membership Pledge", hint: "Non-binding · pending NCUA charter", id: "membership-pledge" },
      { to: "/survey", label: "Member Survey", hint: "10 questions · helps the NCUA charter", id: "member-survey" },
      { to: "/the-case", label: "The Case", id: "the-case" },
      { to: "/tdsp-pump", label: "TDSP Pump", id: "tdsp-pump" },
      { to: "/field-of-membership", label: "FOM Map", id: "fom-map" },
      { to: "/organizers", label: "Organizers", id: "organizers" },
      { to: "/board", label: "Board", id: "board" },
    ],
    flyers: FLYER_LINKS,
  },
];

const linkCls = (active) =>
  `text-xs uppercase tracking-[0.2em] font-semibold whitespace-nowrap transition-colors ${active ? "text-bdu-gold" : "text-bdu-cream/70 hover:text-bdu-gold"}`;
const panelCls = "z-[110] min-w-[16rem] max-w-[19rem] rounded-none border border-bdu-gold/25 bg-bdu-bg/95 backdrop-blur-xl p-1.5 text-bdu-cream shadow-2xl";
const itemCls = "rounded-none flex-col items-start gap-0 px-3 py-2 cursor-pointer focus:bg-bdu-gold/10 focus:text-bdu-gold";

const pathOf = (to) => to.split("#")[0];
const SECTION_RANK = { credit_union: 0, fund: 1, community: 2 };

// Built-in flyer links + admin uploads (menu-enabled, grouped by section) before "All Flyers".
function useFlyerLinks() {
  const uploaded = useUploadedFlyers()
    .filter((f) => f.show_in_menu)
    .sort((a, b) => SECTION_RANK[a.section] - SECTION_RANK[b.section] || a.display_order - b.display_order)
    .map((f) => ({ to: `/flyers#flyer-${f.id}`, label: f.title, hint: `${f.section_label} · ${f.file_type === "pdf" ? "PDF" : "Image"}`, id: `flyer-${f.id}` }));
  return [...FLYER_LINKS.slice(0, -1), ...uploaded, FLYER_LINKS[FLYER_LINKS.length - 1]];
}
const groupActive = (g, pathname) =>
  [...g.items, ...(g.flyers || [])].some((i) => pathOf(i.to) === pathname || (pathOf(i.to) !== "/" && pathname.startsWith(`${pathOf(i.to)}/`)));

function MenuLink({ item }) {
  return (
    <DropdownMenuItem asChild className={itemCls}>
      <Link to={item.to} data-testid={`nav-link-${item.id}`}>
        <span className="text-[11px] uppercase tracking-[0.18em] font-semibold">{item.label}</span>
        {item.hint && <span className="mt-0.5 text-[11px] text-bdu-cream/45">{item.hint}</span>}
      </Link>
    </DropdownMenuItem>
  );
}

function NavMenu({ group, pathname, flyerLinks }) {
  return (
    <DropdownMenu modal={false}>
      <DropdownMenuTrigger
        className={`${linkCls(groupActive(group, pathname))} inline-flex items-center gap-1 outline-none data-[state=open]:text-bdu-gold`}
        data-testid={`nav-menu-${group.key}`}
      >
        {group.label} <ChevronDown size={12} className="transition-transform duration-200 [[data-state=open]>&]:rotate-180" />
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" sideOffset={14} className={panelCls} data-testid={`nav-menu-${group.key}-content`}
                           onCloseAutoFocus={(e) => e.preventDefault()}>
        {group.heading && (
          <DropdownMenuLabel className="px-3 pt-2 pb-2 text-[10px] uppercase tracking-[0.22em] text-bdu-gold font-semibold leading-snug">
            {group.heading}
          </DropdownMenuLabel>
        )}
        {group.items.map((i) => <MenuLink key={i.to} item={i} />)}
        {group.flyers && (
          <>
            <DropdownMenuSeparator className="bg-bdu-gold/15" />
            <DropdownMenuSub>
              <DropdownMenuSubTrigger className="rounded-none px-3 py-2 text-[11px] uppercase tracking-[0.18em] font-semibold cursor-pointer focus:bg-bdu-gold/10 data-[state=open]:bg-bdu-gold/10 data-[state=open]:text-bdu-gold"
                                      data-testid="nav-menu-flyers">
                <FileImage size={14} className="text-bdu-gold" /> Flyers
              </DropdownMenuSubTrigger>
              <DropdownMenuPortal>
                <DropdownMenuSubContent sideOffset={8} className={`${panelCls} max-h-[70vh] overflow-y-auto`} data-testid="nav-menu-flyers-content">
                  {flyerLinks.map((i) => <MenuLink key={i.to} item={i} />)}
                </DropdownMenuSubContent>
              </DropdownMenuPortal>
            </DropdownMenuSub>
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function MobileMenu({ user, onClose, onLogout, flyerLinks }) {
  const item = (i, indent = false) => (
    <Link key={i.to} to={i.to} onClick={onClose} data-testid={`mobile-nav-${i.id}`}
          className={`block py-1.5 text-xs uppercase tracking-[0.2em] font-semibold text-bdu-cream/80 hover:text-bdu-gold ${indent ? "pl-4" : ""}`}>
      {i.label}
    </Link>
  );
  return (
    <div className="md:hidden border-t border-bdu-gold/10 bg-bdu-bg px-6 py-6 space-y-5 max-h-[80vh] overflow-y-auto" data-testid="nav-mobile-menu">
      {NAV.map((g) => g.items ? (
        <div key={g.key}>
          <div className="label-eyebrow mb-1.5">{g.heading || g.label}</div>
          {g.items.map((i) => item(i))}
          {g.flyers && (
            <>
              <div className="mt-2 text-[10px] uppercase tracking-[0.22em] text-bdu-cream/45">Flyers</div>
              {flyerLinks.map((i) => item(i, true))}
            </>
          )}
        </div>
      ) : item(g))}
      {user && user !== false && (
        <NavLink to="/dashboard" onClick={onClose} data-testid="mobile-nav-dashboard" className="block text-xs uppercase tracking-[0.2em] font-semibold text-bdu-cream/80">Dashboard</NavLink>
      )}
      {user?.role === "admin" && (
        <NavLink to="/admin" onClick={onClose} data-testid="mobile-nav-admin" className="block text-xs uppercase tracking-[0.2em] font-semibold text-bdu-red">Admin</NavLink>
      )}
      <div className="pt-4 flex gap-3">
        {user && user !== false ? (
          <button onClick={onLogout} className="btn-ghost w-full" data-testid="mobile-nav-logout">Logout</button>
        ) : (
          <>
            <Link to="/login" onClick={onClose} className="btn-ghost flex-1 text-center" data-testid="mobile-nav-login">Login</Link>
            <Link to="/register" onClick={onClose} className="btn-gold flex-1 text-center" data-testid="mobile-nav-join">Join</Link>
          </>
        )}
      </div>
    </div>
  );
}

export default function Nav() {
  const { user, logout } = useAuth();
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const flyerLinks = useFlyerLinks();

  const handleLogout = async () => {
    await logout();
    setOpen(false);
    navigate("/");
  };

  return (
    <header className="sticky top-0 z-50 bg-black/70 backdrop-blur-xl border-b border-bdu-gold/10">
      <div className="max-w-7xl mx-auto px-6 sm:px-10 py-5 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-3 group shrink-0" data-testid="nav-logo">
          <Monogram size={44} />
          <div className="hidden sm:block">
            <div className="font-serif text-lg text-bdu-cream leading-none tracking-wide whitespace-nowrap">Black Dollars United</div>
            <div className="label-eyebrow mt-1 whitespace-nowrap" style={{ fontSize: "0.55rem" }}>Trillion Dollar Spending Power Fund</div>
          </div>
        </Link>

        <nav className="hidden md:flex flex-1 min-w-0 flex-wrap items-center justify-center gap-x-5 gap-y-3 px-4">
          {NAV.map((g) => g.items ? (
            <NavMenu key={g.key} group={g} pathname={pathname} flyerLinks={flyerLinks} />
          ) : (
            <NavLink key={g.to} to={g.to} end={g.to === "/"} data-testid={`nav-link-${g.id}`} className={({ isActive }) => linkCls(isActive)}>
              {g.label}
            </NavLink>
          ))}
        </nav>

        <div className="hidden md:flex shrink-0 items-center gap-3 pl-4 ml-2 border-l border-bdu-gold/15">
          {user && user !== false ? (
            <DropdownMenu modal={false}>
              <DropdownMenuTrigger className={`${linkCls(pathname === "/dashboard" || pathname === "/admin")} inline-flex items-center gap-1 outline-none data-[state=open]:text-bdu-gold max-w-[160px]`}
                                   data-testid="nav-user-menu">
                <span className="truncate" data-testid="nav-user-name">{user.name}</span> <ChevronDown size={12} />
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" sideOffset={14} className={panelCls} onCloseAutoFocus={(e) => e.preventDefault()}
                                   data-testid="nav-user-menu-content">
                <MenuLink item={{ to: "/dashboard", label: "Dashboard", hint: "Your pledges & activity", id: "dashboard" }} />
                {user.role === "admin" && (
                  <MenuLink item={{ to: "/admin", label: "Admin", hint: "Manage the Fund & Credit Union", id: "admin" }} />
                )}
                <DropdownMenuSeparator className="bg-bdu-gold/15" />
                <DropdownMenuItem onSelect={handleLogout} className={itemCls} data-testid="nav-logout-btn">
                  <span className="text-[11px] uppercase tracking-[0.18em] font-semibold">Logout</span>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <>
              <Link to="/login" className="btn-ghost" data-testid="nav-login-btn">Login</Link>
              <Link to="/register" className="btn-gold" data-testid="nav-register-btn">Join The Fund</Link>
            </>
          )}
        </div>

        <button
          className="md:hidden text-bdu-cream"
          onClick={() => setOpen(!open)}
          data-testid="nav-mobile-toggle"
          aria-label="Toggle menu"
        >
          {open ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>

      {open && <MobileMenu user={user} onClose={() => setOpen(false)} onLogout={handleLogout} flyerLinks={flyerLinks} />}
    </header>
  );
}
